import {FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    CompanyAddressesService,
    CompanyAddressInterface,
    CountryModel,
    CompanyAddressModel,
    CompanyExtendedModel,
    CompanyAddressInputModel,
} from '@spa/api';

import {InArrayValidator} from '@spa/validators';

import {BaseControl, CountryAutocompleteControl, Helper} from '../../../../../shared/classes';

import {CompanyAddressFormErrorStatesEnum} from '../../../enums';

import {
    CompanyAddressControls,
    CompanyAddressSelector,
    CompanyAddressIdSelector,
    CompanyAddressCitySelector,
    CompanyAddressTypeSelector,
    CompanyAddressStateSelector,
    CompanyAddressCountrySelector,
    CompanyAddressPostalCodeSelector
} from '../../../../../../assets/descriptors';

export class CompanyAddressForm {
    private readonly company: CompanyExtendedModel;
    private readonly addressesService: CompanyAddressesService;

    private address: CompanyAddressModel;

    public form: FormGroup;
    public controls: BaseControl[];
    public controlsObservable: BehaviorSubject<BaseControl[]>;

    constructor(
        company: CompanyExtendedModel,
        address: CompanyAddressModel,
        addressesService: CompanyAddressesService
    ) {
        this.company = company;
        this.controls = [];
        this.addressesService = addressesService;
        this.address = address;
    }

    private initControls(
        type: string,
        countries: CountryModel[],
        model?: CompanyAddressModel
    ): BaseControl[] {
        return CompanyAddressControls().map((control: BaseControl) => {
            switch (control.name) {
                case CompanyAddressIdSelector:
                    control.value = !model ? null : model.id;
                    break;
                case CompanyAddressCountrySelector:
                    control.value = !model ? null : model.countryId;

                    const countriesIds = countries.map((country: CountryModel) => country.id);

                    control.validators.push(InArrayValidator(countriesIds));

                    (control as CountryAutocompleteControl).countries = countries;
                    break;
                case CompanyAddressCitySelector:
                    control.value = !model ? null : model.city;
                    break;
                case CompanyAddressStateSelector:
                    control.value = !model ? null : model.stateProvinceCounty;
                    break;
                case CompanyAddressPostalCodeSelector:
                    control.value = !model ? null : model.zipCode;
                    break;
                case CompanyAddressSelector:
                    control.value = !model ? null : model.addressLine;
                    break;
                case CompanyAddressTypeSelector:
                    control.value = type;
                    break;
            }


            return control;
        });
    }

    private updateAddress(): Observable<ApiResponse<ApiError[] | boolean>> {
        const clonedModel = this.address.serialize();

        this.address.countryId = this.form.value[CompanyAddressCountrySelector];
        this.address.city = this.form.value[CompanyAddressCitySelector];
        this.address.stateProvinceCounty = this.form.value[CompanyAddressStateSelector];
        this.address.zipCode = this.form.value[CompanyAddressPostalCodeSelector];
        this.address.addressLine = this.form.value[CompanyAddressSelector];

        return this.addressesService.update(this.address)
            .pipe(
                map((response: ApiResponse<ApiError[] | null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        this.address = new CompanyAddressModel(clonedModel);

                        return Helper.errorsHelper().createErrorResponse<null>(
                            response,
                            CompanyAddressFormErrorStatesEnum.UpdateAddressFailed
                        );
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    private registerAddress(): Observable<ApiResponse<ApiError[] | boolean>> {
        const inputModel = new CompanyAddressInputModel(
            this.company.id,
            this.form.value[CompanyAddressTypeSelector],
            this.form.value[CompanyAddressCountrySelector],
            this.form.value[CompanyAddressCitySelector],
            this.form.value[CompanyAddressStateSelector],
            this.form.value[CompanyAddressPostalCodeSelector],
            this.form.value[CompanyAddressSelector]
        );

        return this.addressesService.register(inputModel)
            .pipe(
                map((response: ApiResponse<ApiError[] | CompanyAddressInterface>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<CompanyAddressInterface>(
                            response,
                            CompanyAddressFormErrorStatesEnum.AddressRegistrationFailed
                        );
                    }

                    const data = response.data as CompanyAddressInterface;

                    this.address = new CompanyAddressModel(data);

                    if (!this.company.addresses) {
                        this.company.addresses = [];
                    }

                    this.company.addresses.push(this.address);

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }

    initForm(type: string, countries: CountryModel[]): void {
        this.form = new FormGroup({});
        this.controls = [];

        if (!this.address) {
            this.controls = this.initControls(type, countries);
        } else {
            this.controls = this.initControls(type, countries, this.address);
        }

        this.controls.map((control: BaseControl) => {
            this.form.addControl(control.name, new FormControl(control.value, control.validators));
        });

        this.controlsObservable = new BehaviorSubject(this.controls);
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        if (this.form.invalid) {
            this.controls.map((control: BaseControl) => {
                const formControl = this.form.get(control.name);

                if (formControl && formControl.invalid) {
                    const errors = Object.keys(formControl.errors).map((errorKey: string) => {
                        return messageFunction(control.placeholder)[errorKey];
                    });

                    control.setErrors(errors);
                }
            });

            return false;
        }

        return true;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> {
        if (this.form.dirty) {
            if (!this.address) {
                return this.registerAddress();
            }

            return this.updateAddress();
        }

        return Helper.createSuccessResponseObservable<boolean>(true);
    }
}
